import {
  CheckCircle2,
  Expand,
  ExternalLink,
  Eye,
  EyeOff,
  FileUp,
  GitBranch,
  Globe,
  Link2,
  Lock,
  Loader2,
  Mic,
  Paperclip,
  Pencil,
  Plus,
  Quote,
  Scale,
  Sparkles,
  Square,
  StickyNote,
  Upload,
  X
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type DragEvent } from "react";
import type { Attachment, EntityLink, ExtractionCandidate, NoteCard, NoteKind, RelationTarget, RelationType, SearchResult, SourceNode } from "../types";
import { findRelationTargets } from "../lib/api";
import { EntityLinksPanel } from "./EntityLinksPanel";
import { MarkdownPreview } from "./MarkdownPreview";
import { NoteEditForm } from "./NoteEditForm";
import { LifecycleBadge, StatusBadge } from "./StatusBadge";
import { defaultStatusForKind, statusOptionsForKind } from "../lib/lifecycle";
import { TagPicker } from "./TagPicker";
import { useSecondClickConfirm } from "../lib/useSecondClickConfirm";

type Props = {
  notes: NoteCard[];
  selectedSource?: SourceNode;
  sources: SourceNode[];
  onCapture: (input: {
    kind: NoteKind;
    content: string;
    sourceId: string;
    relationType?: string;
    relationTargetId?: string;
    status?: string;
    tags?: string[];
    properties?: Record<string, unknown>;
  }) => Promise<void | { objectId?: string }>;
  onUpdateNote: (id: string, input: { content: string }) => Promise<void> | void;
  onUpdateSource: (
    id: string,
    input: { title?: string; kind?: SourceNode["kind"]; progress?: number; parentId?: string | null; status?: string; properties?: Record<string, unknown> }
  ) => Promise<void> | void;
  onCreateRelation: (input: { sourceObjectId: string; relationType: string; targetObjectId: string; properties?: Record<string, unknown> }) => Promise<void>;
  onCreateRelationType: (input: {
    slug: string;
    name: string;
    nameVi?: string;
    description?: string;
    descriptionVi?: string;
    directional?: boolean;
  }) => Promise<void>;
  onSuggest: (objectId: string) => Promise<void>;
  onReviewCandidate: (id: string, decision: "accepted" | "rejected") => Promise<void>;
  onLoadAttachments: (objectId: string) => Promise<void>;
  onUploadAttachment: (
    objectId: string,
    file: File,
    options?: { attachmentKind?: "image" | "file"; source?: "picker" | "paste" | "drop"; sortOrder?: number }
  ) => Promise<void>;
  onDeleteAttachment: (objectId: string, attachmentId: string) => Promise<void>;
  onLoadLinks: (objectId: string) => Promise<void>;
  onCreateLink: (objectId: string, input: { url: string; label?: string }) => Promise<void>;
  onDeleteLink: (objectId: string, linkId: string) => Promise<void>;
  onTranscribeAttachment: (noteId: string, attachmentId: string) => Promise<void>;
  onSetVisibility?: (objectId: string, visibility: "public" | "private") => Promise<void>;
  candidates: ExtractionCandidate[];
  attachmentsByObject: Record<string, Attachment[]>;
  linksByObject: Record<string, EntityLink[]>;
  searchResults: SearchResult[];
  relationTypes: RelationType[];
  captureError?: string;
  relationError?: string;
  aiError?: string;
  attachmentError?: string;
  transcribeError?: string;
  linkError?: string;
  searchError?: string;
};

type TargetOption = {
  id: string;
  title: string;
  kind: string;
  group: "source" | "note";
  depth: number;
  searchText: string;
  context?: string;
  content?: string;
  alreadyLinked?: boolean;
};

type PendingImageAttachment = {
  id: string;
  file: File;
  preview: string;
  source: "picker" | "paste" | "drop";
};

const maxImageBytes = 10 * 1024 * 1024;
const noteLikeKinds = new Set(["note", "task", "quote", "exercise", "practice", "reflection", "critique", "expansion"]);

function isNoteLikeKind(kind: string) {
  return noteLikeKinds.has(kind);
}

function imageValidationError(file: File) {
  if (!file.type.startsWith("image/")) {
    return `${file.name}: chỉ chấp nhận ảnh`;
  }
  if (file.size > maxImageBytes) {
    return `${file.name}: ảnh quá lớn (tối đa 10MB)`;
  }
  return "";
}

function ImageAttachmentPicker({
  images,
  disabled,
  pasteEnabled,
  uploadProgress,
  onAddFiles,
  onRemove
}: {
  images: PendingImageAttachment[];
  disabled?: boolean;
  pasteEnabled: boolean;
  uploadProgress?: string;
  onAddFiles: (files: File[], source: PendingImageAttachment["source"]) => void;
  onRemove: (id: string) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!pasteEnabled || disabled) {
      return;
    }

    const onPaste = (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items) {
        return;
      }

      const files: File[] = [];
      for (const item of Array.from(items)) {
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }

      if (files.length > 0) {
        event.preventDefault();
        onAddFiles(files, "paste");
      }
    };

    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [disabled, onAddFiles, pasteEnabled]);

  const selectFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (files.length > 0) {
      onAddFiles(files, "picker");
    }
    event.target.value = "";
  };

  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const files = Array.from(event.dataTransfer.files ?? []);
    if (files.length > 0) {
      onAddFiles(files, "drop");
    }
  };

  const onDragOver = (event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div>
      <input ref={fileInputRef} className="hidden" type="file" accept="image/*" multiple disabled={disabled} onChange={selectFiles} />
      {images.length > 0 ? (
        <div className="mb-3 grid grid-cols-3 gap-2 md:grid-cols-4">
          {images.map((image) => (
            <div key={image.id} className="relative aspect-square overflow-hidden rounded-lg border border-line bg-slate-50">
              <img src={image.preview} alt="" className="h-full w-full object-cover" />
              <button
                className="absolute right-1 top-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600 disabled:bg-slate-300"
                type="button"
                disabled={disabled}
                aria-label="Bỏ ảnh"
                onClick={() => onRemove(image.id)}
              >
                <X size={12} aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      ) : null}
      <button
        className="flex w-full flex-col items-center gap-1 rounded-lg border-2 border-dashed border-line px-4 py-4 text-sm font-semibold text-muted transition-colors hover:border-blue-300 hover:text-brand disabled:cursor-not-allowed disabled:opacity-60"
        type="button"
        disabled={disabled}
        onClick={() => fileInputRef.current?.click()}
        onDrop={onDrop}
        onDragOver={onDragOver}
      >
        <Upload size={20} aria-hidden="true" />
        <span>
          Kéo thả, chọn ảnh, hoặc <strong>Ctrl+V</strong> paste
        </span>
        <span className="text-xs font-medium text-slate-400">Tối đa 10MB/ảnh</span>
      </button>
      {uploadProgress ? <p className="mt-2 text-xs font-bold text-brand">{uploadProgress}</p> : null}
    </div>
  );
}

const kindOptions: Array<{ kind: NoteKind; label: string; icon: typeof Plus }> = [
  { kind: "note", label: "Note", icon: Plus },
  { kind: "quote", label: "Quote", icon: Quote },
  { kind: "practice", label: "Practice", icon: CheckCircle2 },
  { kind: "critique", label: "Critique", icon: Scale },
  { kind: "expansion", label: "Expansion", icon: Expand },
  { kind: "reflection", label: "Reflection", icon: StickyNote }
];

const fallbackRelationTypes: RelationType[] = [
  { id: "relation_belongs_to", slug: "belongs_to", name: "Belongs to", nameVi: "Thuộc về", description: "", descriptionVi: "Mục này thuộc về một nguồn hoặc ngữ cảnh lớn hơn.", directional: true, system: true },
  { id: "relation_mentions", slug: "mentions", name: "Mentions", nameVi: "Nhắc tới", description: "", descriptionVi: "Note nhắc tới một concept, người, sự kiện hoặc thực thể.", directional: true, system: true },
  { id: "relation_supports", slug: "supports", name: "Supports", nameVi: "Ủng hộ", description: "", descriptionVi: "Note này củng cố hoặc làm rõ một ý khác.", directional: true, system: true },
  { id: "relation_contradicts", slug: "contradicts", name: "Contradicts", nameVi: "Mâu thuẫn với", description: "", descriptionVi: "Note này mâu thuẫn hoặc phản biện một ý khác.", directional: false, system: true },
  { id: "relation_turns_into_task", slug: "turns_into_task", name: "Turns into task", nameVi: "Tạo việc cần làm", description: "", descriptionVi: "Note này tạo ra một việc cần làm tiếp theo.", directional: true, system: true },
  { id: "relation_enriches", slug: "enriches", name: "Enriches", nameVi: "Làm giàu", description: "", descriptionVi: "Note mới bổ sung bối cảnh hoặc làm giàu note/concept cũ.", directional: true, system: true }
];

const systemRelationVi: Record<string, { name: string; description: string }> = {
  contains: { name: "Chứa", description: "Nguồn cha chứa nguồn con, ví dụ sách chứa chương hoặc khóa học chứa bài học." },
  belongs_to: { name: "Thuộc về", description: "Mục này thuộc về một nguồn hoặc ngữ cảnh lớn hơn." },
  cites: { name: "Trích dẫn", description: "Note hoặc quote trích dẫn một nguồn, link hoặc đoạn gốc." },
  mentions: { name: "Nhắc tới", description: "Note nhắc tới một concept, người, sự kiện hoặc thực thể." },
  supports: { name: "Ủng hộ", description: "Note này củng cố hoặc làm rõ một ý khác." },
  contradicts: { name: "Mâu thuẫn với", description: "Note này mâu thuẫn hoặc phản biện một ý khác." },
  turns_into_task: { name: "Tạo việc cần làm", description: "Note này tạo ra một việc cần làm tiếp theo." },
  practices: { name: "Luyện tập", description: "Practice này dùng để luyện một concept, task hoặc bài tập." },
  enriches: { name: "Làm giàu", description: "Note mới bổ sung bối cảnh hoặc làm giàu note/concept cũ." },
  produced_from: { name: "Được tạo ra từ", description: "Output này được tổng hợp từ các note/nguồn đầu vào." }
};

const sourceTargetKinds = new Set(["course", "module", "lesson", "book", "article", "topic", "youtube", "output", "link"]);

function relationLabel(type?: RelationType) {
  return type?.nameVi || (type?.slug ? systemRelationVi[type.slug]?.name : "") || type?.name || type?.slug || "";
}

function relationDescription(type?: RelationType) {
  return type?.descriptionVi || (type?.slug ? systemRelationVi[type.slug]?.description : "") || type?.description || "";
}

function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function titleFromSlug(value: string) {
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function plainTargetText(value?: string, max = 180) {
  const text = (value ?? "")
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/[#>*_`[\]()~-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return text.length > max ? `${text.slice(0, max - 3)}...` : text;
}

function sourceDepth(source: SourceNode, sourceById: Map<string, SourceNode>) {
  let depth = 0;
  let parentId = source.parentId ?? "";
  const seen = new Set<string>();

  while (parentId && !seen.has(parentId)) {
    seen.add(parentId);
    const parent = sourceById.get(parentId);
    if (!parent) {
      break;
    }
    depth += 1;
    parentId = parent.parentId ?? "";
  }

  return depth;
}

function sourcePath(source: SourceNode | undefined, sourceById: Map<string, SourceNode>) {
  if (!source) {
    return "";
  }

  const path = [source.title];
  let parentId = source.parentId ?? "";
  const seen = new Set<string>();

  while (parentId && !seen.has(parentId)) {
    seen.add(parentId);
    const parent = sourceById.get(parentId);
    if (!parent) {
      break;
    }
    path.unshift(parent.title);
    parentId = parent.parentId ?? "";
  }

  return path.join(" / ");
}

function filterTargets(options: TargetOption[], query: string, excludedId?: string) {
  const normalized = query.trim().toLowerCase();
  return options.filter((option) => option.id !== excludedId && (!normalized || option.searchText.includes(normalized)));
}

export function CapturePanel({
  notes,
  selectedSource,
  sources,
  onCapture,
  onUpdateNote,
  onUpdateSource,
  onCreateRelation,
  onCreateRelationType,
  onSuggest,
  onLoadAttachments,
  onUploadAttachment,
  onDeleteAttachment,
  onLoadLinks,
  onCreateLink,
  onDeleteLink,
  onTranscribeAttachment,
  onSetVisibility,
  attachmentsByObject,
  linksByObject,
  searchResults,
  relationTypes,
  captureError,
  relationError,
  aiError,
  attachmentError,
  transcribeError,
  linkError,
  searchError
}: Props) {
  const [kind, setKind] = useState<NoteKind>("note");
  const [status, setStatus] = useState(() => defaultStatusForKind("note"));
  const [tags, setTags] = useState<string[]>(selectedSource?.tags ?? []);
  const [content, setContent] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [goalEditing, setGoalEditing] = useState(false);
  const [goalDraft, setGoalDraft] = useState("");
  const [sourceStatusDraft, setSourceStatusDraft] = useState("");
  const [goalSaving, setGoalSaving] = useState(false);
  const [goalError, setGoalError] = useState("");
  const [relationOpen, setRelationOpen] = useState(false);
  const [relationType, setRelationType] = useState("belongs_to");
  const [relationTarget, setRelationTarget] = useState("");
  const [relationTargetSearch, setRelationTargetSearch] = useState("");
  const [createRelationOpen, setCreateRelationOpen] = useState(false);
  const [newRelationSlug, setNewRelationSlug] = useState("");
  const [newRelationNameVi, setNewRelationNameVi] = useState("");
  const [newRelationDescriptionVi, setNewRelationDescriptionVi] = useState("");
  const [isCreatingRelationType, setIsCreatingRelationType] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState("");
  const [connectNoteId, setConnectNoteId] = useState("");
  const [connectType, setConnectType] = useState("mentions");
  const [connectTargetId, setConnectTargetId] = useState("");
  const [connectTargetSearch, setConnectTargetSearch] = useState("");
  const [connectRemoteTargets, setConnectRemoteTargets] = useState<TargetOption[]>([]);
  const [connectTargetLoading, setConnectTargetLoading] = useState(false);
  const [isRelationSaving, setIsRelationSaving] = useState(false);
  const [suggestingId, setSuggestingId] = useState("");
  const [attachmentOpenId, setAttachmentOpenId] = useState("");
  const [linkOpenId, setLinkOpenId] = useState("");
  const [uploadingId, setUploadingId] = useState("");
  const [imageError, setImageError] = useState("");
  const [imageUploadProgress, setImageUploadProgress] = useState("");
  const [pendingImages, setPendingImages] = useState<PendingImageAttachment[]>([]);
  const pendingImagesRef = useRef<PendingImageAttachment[]>([]);
  const attachmentDeleteConfirm = useSecondClickConfirm();

  // RM-D: voice — record or upload audio. The file is held as a PENDING attachment and is only
  // persisted when the note is saved (Save note), then attached to that note. Transcription is a
  // separate, per-file action on the saved attachment (see the attachment list).
  const [isRecording, setIsRecording] = useState(false);
  const [voiceError, setVoiceError] = useState("");
  const [transcribingId, setTranscribingId] = useState("");
  const [pendingAudio, setPendingAudio] = useState<Array<{ id: string; blob: Blob; name: string }>>([]);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  const addPendingAudio = (blob: Blob, name: string) => {
    setVoiceError("");
    setPendingAudio((list) => [...list, { id: crypto.randomUUID(), blob, name }]);
  };

  const removePendingAudio = (id: string) => {
    setPendingAudio((list) => list.filter((item) => item.id !== id));
  };

  const buildPendingImages = useCallback((files: File[], source: PendingImageAttachment["source"]) => {
    const next: PendingImageAttachment[] = [];
    const errors: string[] = [];

    files.forEach((file) => {
      const error = imageValidationError(file);
      if (error) {
        errors.push(error);
        return;
      }
      next.push({ id: crypto.randomUUID(), file, preview: URL.createObjectURL(file), source });
    });

    if (errors.length > 0) {
      setImageError(errors[0]);
    }
    if (next.length > 0) {
      setImageError("");
    }

    return next;
  }, []);

  const addPendingImages = useCallback((files: File[], source: PendingImageAttachment["source"]) => {
    const next = buildPendingImages(files, source);
    if (next.length > 0) {
      setPendingImages((list) => [...list, ...next]);
    }
  }, [buildPendingImages]);

  const removePendingImage = (id: string) => {
    setPendingImages((list) => {
      const image = list.find((item) => item.id === id);
      if (image) {
        URL.revokeObjectURL(image.preview);
      }
      return list.filter((item) => item.id !== id);
    });
  };

  const removeUploadedPendingImage = (id: string) => {
    setPendingImages((list) => {
      const image = list.find((item) => item.id === id);
      if (image) {
        URL.revokeObjectURL(image.preview);
      }
      return list.filter((item) => item.id !== id);
    });
  };

  const uploadPendingImages = async (noteId: string, images: PendingImageAttachment[]) => {
    if (images.length === 0) {
      return;
    }

    try {
      for (let index = 0; index < images.length; index += 1) {
        const image = images[index];
        setImageUploadProgress(`Đang upload ${index + 1}/${images.length}...`);
        await onUploadAttachment(noteId, image.file, { attachmentKind: "image", source: image.source, sortOrder: index });
        removeUploadedPendingImage(image.id);
      }
    } finally {
      setImageUploadProgress("");
    }
  };

  useEffect(() => {
    pendingImagesRef.current = pendingImages;
  }, [pendingImages]);

  useEffect(() => {
    return () => {
      pendingImagesRef.current.forEach((image) => URL.revokeObjectURL(image.preview));
    };
  }, []);

  const startRecording = async () => {
    setVoiceError("");
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setVoiceError("Trình duyệt không hỗ trợ ghi âm. Hãy tải file audio lên.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      audioChunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      recorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType || "audio/webm" });
        stream.getTracks().forEach((track) => track.stop());
        addPendingAudio(blob, `ghi-am-${Date.now()}.webm`);
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
    } catch {
      setVoiceError("Không truy cập được micro. Kiểm tra quyền hoặc tải file audio lên.");
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    mediaRecorderRef.current = null;
    setIsRecording(false);
  };

  const runTranscribe = async (noteId: string, attachmentId: string) => {
    setTranscribingId(attachmentId);
    try {
      await onTranscribeAttachment(noteId, attachmentId);
    } finally {
      setTranscribingId("");
    }
  };

  const visibleNotes = useMemo(() => {
    const scoped = !selectedSource
      ? notes
      : notes.filter((note) => note.sourceId === selectedSource.id || selectedSource.kind === "course");
    // Cap the in-context list so the capture screen stays lean (notes are newest-first).
    return scoped.slice(0, 12);
  }, [notes, selectedSource]);

  const relationOptions = relationTypes.length ? relationTypes : fallbackRelationTypes;
  const selectedRelation = relationOptions.find((option) => option.slug === relationType);
  const selectedConnectRelation = relationOptions.find((option) => option.slug === connectType);

  const targetOptions = useMemo<TargetOption[]>(() => {
    const sourceById = new Map(sources.map((source) => [source.id, source]));
    const sourceTargets = sources
      .map((source) => {
        const depth = sourceDepth(source, sourceById);
        const parent = source.parentId ? sourceById.get(source.parentId) : null;
        const path = parent ? `${parent.title} / ${source.title}` : source.title;
        return {
          id: source.id,
          title: source.title,
          kind: source.kind,
          group: "source" as const,
          depth,
          searchText: `${source.title} ${source.kind} ${path}`.toLowerCase()
        };
      })
      .sort((left, right) => left.group.localeCompare(right.group) || left.depth - right.depth || left.title.localeCompare(right.title));

    const noteTargets = notes.map((note) => ({
      id: note.id,
      title: note.title,
      kind: note.kind,
      group: "note" as const,
      depth: 0,
      searchText: `${note.title} ${note.kind} ${note.content}`.toLowerCase()
    }));

    return [...sourceTargets, ...noteTargets];
  }, [notes, sources]);

  const relationTargetOptions = filterTargets(targetOptions, relationTargetSearch);
  const connectTargetOptions = filterTargets(targetOptions, connectTargetSearch, connectNoteId);
  const selectedRelationTarget =
    targetOptions.find((target) => target.id === relationTarget) ?? targetOptions.find((target) => target.id === (selectedSource?.id ?? sources[0]?.id));
  const activeConnectTargetOptions = connectRemoteTargets.length || connectTargetLoading || connectTargetSearch.trim()
    ? connectRemoteTargets
    : connectTargetOptions.slice(0, 12);
  const activeConnectNote = useMemo(() => notes.find((note) => note.id === connectNoteId), [connectNoteId, notes]);
  const selectedConnectTarget =
    activeConnectTargetOptions.find((target) => target.id === connectTargetId) ??
    targetOptions.find((target) => target.id === connectTargetId);
  const sourceById = useMemo(() => new Map(sources.map((source) => [source.id, source])), [sources]);
  const selectedSourcePath = sourcePath(selectedSource, sourceById);
  const selectedSourceGoal = typeof selectedSource?.properties?.current_goal === "string" ? selectedSource.properties.current_goal : "";

  useEffect(() => {
    setGoalDraft(selectedSourceGoal);
    setSourceStatusDraft(selectedSource?.status ?? defaultStatusForKind(selectedSource?.kind ?? "book"));
    setGoalEditing(false);
    setGoalError("");
  }, [selectedSource?.id, selectedSource?.kind, selectedSource?.status, selectedSourceGoal]);

  useEffect(() => {
    if (!connectNoteId) {
      setConnectRemoteTargets([]);
      setConnectTargetLoading(false);
      return;
    }

    let cancelled = false;
    setConnectTargetLoading(true);

    findRelationTargets({ objectId: connectNoteId, q: connectTargetSearch, limit: 12 })
      .then((payload) => {
        if (cancelled) {
          return;
        }
        setConnectRemoteTargets(
          (payload.data ?? []).map((target: RelationTarget) => ({
            id: target.objectId,
            title: target.title,
            kind: target.kind,
            group: sourceTargetKinds.has(target.kind) ? "source" : "note",
            depth: 0,
            searchText: `${target.title} ${target.kind} ${target.context ?? ""} ${target.content ?? ""}`.toLowerCase(),
            context: target.context,
            content: target.content,
            alreadyLinked: target.alreadyLinked
          }))
        );
      })
      .catch(() => {
        if (!cancelled) {
          setConnectRemoteTargets([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setConnectTargetLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [connectNoteId, connectTargetSearch]);

  // Pre-fill tags from the source the note will attach to (copy-on-create inheritance), so
  // adding a tag at capture appends to the inherited set instead of replacing it.
  const captureSourceId = selectedSource?.id ?? sources[0]?.id;
  useEffect(() => {
    setTags(sources.find((node) => node.id === captureSourceId)?.tags ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captureSourceId]);

  const availableTags = useMemo(() => {
    const set = new Set<string>();
    sources.forEach((node) => (node.tags ?? []).forEach((tag) => set.add(tag)));
    notes.forEach((note) => (note.tags ?? []).forEach((tag) => set.add(tag)));
    return Array.from(set).sort();
  }, [sources, notes]);

  const handleCapture = async () => {
    const sourceId = selectedSource?.id ?? sources[0]?.id;
    const normalized = content.trim();

    if (!sourceId || !normalized) {
      return;
    }

    setIsSaving(true);

    try {
      const result = await onCapture({
        kind,
        content: normalized,
        sourceId,
        status,
        tags,
        relationType: relationOpen ? relationType : "belongs_to",
        relationTargetId: relationOpen ? relationTarget || undefined : sourceId
      });

      // Attach pending media to the note we just created. Attachments always belong to a note.
      const objectId = result && typeof result === "object" ? result.objectId : undefined;
      if (objectId && pendingAudio.length > 0) {
        for (const item of pendingAudio) {
          const file = new File([item.blob], item.name, { type: item.blob.type || "audio/webm" });
          await onUploadAttachment(objectId, file);
        }
        await onLoadAttachments(objectId);
        setAttachmentOpenId(objectId);
        setPendingAudio([]);
      }
      if (objectId && pendingImages.length > 0) {
        await uploadPendingImages(objectId, [...pendingImages]);
        await onLoadAttachments(objectId);
        setAttachmentOpenId(objectId);
      }

      setContent("");
      setRelationTarget("");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveSourceGoal = async () => {
    if (!selectedSource) {
      return;
    }

    setGoalSaving(true);
    setGoalError("");

    try {
      await onUpdateSource(selectedSource.id, {
        status: sourceStatusDraft || selectedSource.status,
        properties: {
          ...(selectedSource.properties ?? {}),
          current_goal: goalDraft.trim()
        }
      });
      setGoalEditing(false);
    } catch (error) {
      setGoalError(error instanceof Error ? error.message : "Update source goal failed");
    } finally {
      setGoalSaving(false);
    }
  };

  const handleCreateRelationType = async () => {
    const slug = slugify(newRelationSlug);

    if (!slug || !newRelationNameVi.trim()) {
      return;
    }

    setIsCreatingRelationType(true);

    try {
      await onCreateRelationType({
        slug,
        name: newRelationNameVi.trim() || titleFromSlug(slug),
        nameVi: newRelationNameVi.trim(),
        descriptionVi: newRelationDescriptionVi.trim(),
        directional: true
      });
      setRelationType(slug);
      setConnectType(slug);
      setNewRelationSlug("");
      setNewRelationNameVi("");
      setNewRelationDescriptionVi("");
      setCreateRelationOpen(false);
    } finally {
      setIsCreatingRelationType(false);
    }
  };

  const handleCreateRelation = async (noteId: string) => {
    if (!connectTargetId) {
      return;
    }

    setIsRelationSaving(true);

    try {
      await onCreateRelation({
        sourceObjectId: noteId,
        relationType: connectType,
        targetObjectId: connectTargetId
      });
      setConnectNoteId("");
      setConnectTargetId("");
      setConnectTargetSearch("");
      setConnectRemoteTargets([]);
      setConnectType("mentions");
    } finally {
      setIsRelationSaving(false);
    }
  };

  const handleSuggest = async (noteId: string) => {
    setSuggestingId(noteId);

    try {
      await onSuggest(noteId);
    } finally {
      setSuggestingId("");
    }
  };

  const toggleAttachments = async (noteId: string) => {
    const nextId = attachmentOpenId === noteId ? "" : noteId;
    setAttachmentOpenId(nextId);

    if (nextId && !attachmentsByObject[nextId]) {
      await onLoadAttachments(nextId);
    }
  };

  const toggleLinks = async (noteId: string) => {
    const nextId = linkOpenId === noteId ? "" : noteId;
    setLinkOpenId(nextId);

    if (nextId && !linksByObject[nextId]) {
      await onLoadLinks(nextId);
    }
  };

  const handleDeleteAttachment = async (noteId: string, attachmentId: string) => {
    setUploadingId(noteId);

    try {
      await onDeleteAttachment(noteId, attachmentId);
    } finally {
      setUploadingId("");
    }
  };

  return (
    <div className="space-y-4">
      <section className="rounded-lg border border-line bg-white shadow-panel">
        <div className="border-b border-line px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-lg font-bold">Ghi chép</h1>
              <p className="truncate text-sm font-medium text-muted">
                {selectedSourcePath || "Chọn nguồn học để gắn ngữ cảnh"}
              </p>
            </div>
            {selectedSource ? (
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                <StatusBadge kind={selectedSource.kind} />
                <LifecycleBadge kind={selectedSource.kind} status={selectedSource.status} />
                <button
                  className={`inline-flex h-8 w-8 items-center justify-center rounded-lg border ${
                    goalEditing ? "border-blue-200 bg-blue-50 text-brand" : "border-line bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                  type="button"
                  aria-label="Sửa mục tiêu và trạng thái nguồn"
                  title="Sửa mục tiêu và trạng thái nguồn"
                  onClick={() => {
                    setGoalDraft(selectedSourceGoal);
                    setSourceStatusDraft(selectedSource.status ?? defaultStatusForKind(selectedSource.kind));
                    setGoalEditing((value) => !value);
                    setGoalError("");
                  }}
                >
                  <Pencil size={14} aria-hidden="true" />
                </button>
              </div>
            ) : null}
          </div>
        </div>

        <div className="p-4">
          {goalEditing && selectedSource ? (
            <div className="mb-3 rounded-lg border border-blue-100 bg-blue-50 p-3">
              <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_260px]">
                <label className="block">
                  <span className="mb-1 block text-xs font-bold text-slate-600">Mục tiêu hiện tại</span>
                  <textarea
                    id="capture-source-goal"
                    className="min-h-20 w-full resize-y rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm leading-6 text-ink placeholder:text-slate-400"
                    value={goalDraft}
                    onChange={(event) => setGoalDraft(event.target.value)}
                    placeholder="Ví dụ: Lần này chỉ đọc để nắm concept chính."
                  />
                </label>
                <div>
                  <p className="mb-1 text-xs font-bold text-slate-600">Trạng thái</p>
                  <div className="flex flex-wrap gap-2">
                    {statusOptionsForKind(selectedSource.kind).map((option) => (
                      <button
                        key={option.slug}
                        className={`inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-[11px] font-bold transition-colors ${
                          sourceStatusDraft === option.slug
                            ? "border-slate-300 bg-white text-ink"
                            : "border-blue-100 bg-blue-50 text-slate-600 hover:bg-white"
                        }`}
                        type="button"
                        onClick={() => setSourceStatusDraft(option.slug)}
                      >
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: option.dotColor }} />
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  className="inline-flex h-8 items-center gap-2 rounded-lg bg-brand px-3 text-xs font-bold text-white hover:bg-blue-700 disabled:bg-slate-300"
                  type="button"
                  disabled={goalSaving}
                  onClick={() => void handleSaveSourceGoal()}
                >
                  {goalSaving ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <CheckCircle2 size={13} aria-hidden="true" />}
                  {goalSaving ? "Đang lưu" : "Lưu"}
                </button>
                <button
                  className="inline-flex h-8 items-center gap-2 rounded-lg border border-line bg-white px-3 text-xs font-bold text-slate-600 hover:bg-slate-50"
                  type="button"
                  disabled={goalSaving}
                  onClick={() => {
                    setGoalDraft(selectedSourceGoal);
                    setSourceStatusDraft(selectedSource.status ?? defaultStatusForKind(selectedSource.kind));
                    setGoalEditing(false);
                    setGoalError("");
                  }}
                >
                  Hủy
                </button>
                {goalError ? <p className="text-xs font-semibold text-red-700">{goalError}</p> : null}
              </div>
            </div>
          ) : null}

          <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-6">
            {kindOptions.map((option) => (
              <button
                key={option.kind}
                className={`inline-flex h-11 items-center justify-center gap-2 rounded-lg border px-3 text-sm font-semibold transition ${
                  kind === option.kind ? "border-orange-200 bg-orange-50 text-focus" : "border-line bg-white text-slate-600 hover:bg-slate-50"
                }`}
                type="button"
                onClick={() => {
                  setKind(option.kind);
                  setStatus(defaultStatusForKind(option.kind));
                }}
              >
                <option.icon size={16} aria-hidden="true" />
                {option.label}
              </button>
            ))}
          </div>

          <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-bold text-muted">Status</span>
              {statusOptionsForKind(kind).map((option) => (
                <button
                  key={option.slug}
                  className={`inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-[11px] font-bold transition-colors ${
                    status === option.slug
                      ? "border-slate-300 bg-slate-100 text-ink"
                      : "border-line bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                  type="button"
                  onClick={() => setStatus(option.slug)}
                >
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: option.dotColor }} />
                  {option.label}
                </button>
              ))}
            </div>
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="text-xs font-bold text-muted">Tags</span>
              <TagPicker tags={tags} availableTags={availableTags} onChange={setTags} />
            </div>
          </div>

          <div className="mb-3 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs leading-5 text-slate-700">
            <p className="font-normal text-ink">Markdown nhanh</p>
            <p className="mt-1">Dùng # Tiêu đề, - bullet, **đậm**, *nghiêng*, [link](https://...), &gt; quote, `code`.</p>
          </div>

          <textarea
            className="min-h-72 w-full resize-y rounded-lg border border-line bg-slate-50 p-3 text-sm leading-6 text-ink placeholder:text-slate-400"
            value={content}
            onChange={(event) => setContent(event.target.value)}
            placeholder="Ghi note bằng Markdown."
          />

          {previewOpen ? (
            <div className="mt-3 min-h-40 rounded-lg border border-line bg-white p-3">
              <div className="mb-2 flex items-center justify-between gap-2 border-b border-line pb-2">
                <span className="text-xs uppercase tracking-wide text-muted">Preview</span>
                <span className="text-xs text-muted">{content.trim().length} ký tự</span>
              </div>
              <MarkdownPreview content={content} emptyText="Preview Markdown sẽ hiện ở đây." />
            </div>
          ) : null}

          <div className="mt-3 grid gap-3 lg:grid-cols-2">
            <div className="rounded-lg border border-line bg-white px-3 py-2.5">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-xs font-bold text-slate-700">Ảnh đính kèm note</p>
                  <p className="text-[11px] font-medium text-muted">Ảnh sẽ được lưu cùng note sau khi bấm Save note.</p>
                </div>
                {pendingImages.length > 0 ? (
                  <span className="rounded-md border border-blue-100 bg-blue-50 px-2 py-1 text-[11px] font-bold text-brand">
                    {pendingImages.length} ảnh chờ lưu
                  </span>
                ) : null}
              </div>
              <ImageAttachmentPicker
                images={pendingImages}
                disabled={isSaving}
                pasteEnabled
                uploadProgress={isSaving ? imageUploadProgress : ""}
                onAddFiles={addPendingImages}
                onRemove={removePendingImage}
              />
              {imageError ? <p className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">{imageError}</p> : null}
            </div>

            <div className="rounded-lg border border-line bg-white px-3 py-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold text-muted">Ghi chú bằng giọng nói</span>
                {!isRecording ? (
                  <button
                    className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-line bg-white px-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    type="button"
                    onClick={startRecording}
                  >
                    <Mic size={14} aria-hidden="true" /> Ghi âm
                  </button>
                ) : (
                  <button
                    className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-2.5 text-xs font-semibold text-rose-700"
                    type="button"
                    onClick={stopRecording}
                  >
                    <Square size={13} aria-hidden="true" /> Dừng ghi
                  </button>
                )}
                <label className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-lg border border-line bg-white px-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                  <FileUp size={14} aria-hidden="true" /> Tải audio
                  <input
                    className="hidden"
                    type="file"
                    accept="audio/*"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      event.target.value = "";
                      if (file) {
                        addPendingAudio(file, file.name);
                      }
                    }}
                  />
                </label>
                {isRecording ? (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-rose-600">
                    <span className="h-2 w-2 animate-pulse rounded-full bg-rose-500" />
                    Đang ghi…
                  </span>
                ) : null}
              </div>
              {pendingAudio.length > 0 ? (
                <div className="mt-2 space-y-1.5">
                  {pendingAudio.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between gap-3 rounded-md border border-line bg-slate-50 px-2.5 py-1.5 text-xs font-semibold text-slate-700"
                    >
                      <span className="min-w-0 flex-1 truncate" title={item.name}>
                        {item.name}
                      </span>
                      <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-700">chưa lưu</span>
                      <button className="shrink-0 text-red-600 hover:text-red-700" type="button" onClick={() => removePendingAudio(item.id)}>
                        Bỏ
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
              {voiceError ? <p className="mt-1.5 text-xs font-medium text-rose-600">{voiceError}</p> : null}
              <p className="mt-1 text-[11px] text-muted">
                Bản ghi/audio được giữ tạm ở đây. Bấm <span className="font-semibold">Save note</span> để lưu note kèm file. Sau đó mở
                “Files đính kèm” của note rồi bấm <span className="font-semibold">Transcript</span> để chuyển thành chữ.
              </p>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              <button
                className="inline-flex h-9 items-center gap-2 rounded-lg border border-line bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                type="button"
                onClick={() => setRelationOpen((value) => !value)}
              >
                <Link2 size={16} aria-hidden="true" />
                Add relation
              </button>
              <button
                className={`inline-flex h-9 w-9 items-center justify-center rounded-lg border ${
                  previewOpen ? "border-blue-200 bg-blue-50 text-brand" : "border-line bg-white text-slate-700 hover:bg-slate-50"
                }`}
                type="button"
                aria-label={previewOpen ? "Ẩn preview" : "Mở preview"}
                onClick={() => setPreviewOpen((value) => !value)}
              >
                {previewOpen ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
              </button>
            </div>
            <button
              className="inline-flex h-10 items-center gap-2 rounded-lg bg-brand px-4 text-sm font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
              type="button"
              disabled={!content.trim() || isSaving}
              onClick={handleCapture}
            >
              <Plus size={17} aria-hidden="true" />
              {isSaving ? "Đang lưu" : "Save note"}
            </button>
          </div>

          {captureError ? <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">{captureError}</p> : null}

          {relationOpen ? (
            <div className="mt-4 rounded-lg border border-orange-200 bg-orange-50 p-3">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-orange-200 bg-white text-focus">
                  <GitBranch size={16} aria-hidden="true" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-bold">Relation khi ghi note</p>
                      <p className="mt-1 text-xs font-medium text-slate-600">
                        Chọn loại quan hệ và target. Nếu chưa chọn target, note vẫn được gắn với source đang mở.
                      </p>
                    </div>
                    <button
                      className="inline-flex h-8 items-center gap-2 rounded-lg border border-orange-200 bg-white px-3 text-xs font-bold text-focus hover:bg-orange-100"
                      type="button"
                      onClick={() => setCreateRelationOpen((value) => !value)}
                    >
                      <Plus size={14} aria-hidden="true" />
                      Thêm relation
                    </button>
                  </div>

                  <div className="mt-3 grid gap-2 lg:grid-cols-[220px_minmax(0,1fr)]">
                    <div>
                      <select
                        className="h-10 w-full rounded-lg border border-orange-200 bg-white px-3 text-sm font-semibold text-slate-700"
                        value={relationType}
                        onChange={(event) => setRelationType(event.target.value)}
                        aria-label="Relation type"
                      >
                        {relationOptions.map((option) => (
                          <option key={option.id} value={option.slug}>
                            {relationLabel(option)}
                          </option>
                        ))}
                      </select>
                      {selectedRelation ? (
                        <p className="mt-1 text-xs font-semibold text-slate-600" title={selectedRelation.slug}>
                          {relationDescription(selectedRelation) || selectedRelation.slug}
                        </p>
                      ) : null}
                    </div>

                    <div className="space-y-2">
                      <input
                        className="h-10 w-full rounded-lg border border-orange-200 bg-white px-3 text-sm font-semibold text-slate-700"
                        value={relationTargetSearch}
                        onChange={(event) => setRelationTargetSearch(event.target.value)}
                        placeholder="Tìm target theo tên source, mục con hoặc note"
                        aria-label="Search relation target"
                      />
                      <select
                        className="h-10 w-full rounded-lg border border-orange-200 bg-white px-3 text-sm font-semibold text-slate-700"
                        value={relationTarget}
                        onChange={(event) => setRelationTarget(event.target.value)}
                        aria-label="Relation target"
                      >
                        <option value="">Target: source đang chọn hoặc chọn sau</option>
                        <optgroup label="Sources">
                          {relationTargetOptions
                            .filter((target) => target.group === "source")
                            .map((target) => (
                              <option key={target.id} value={target.id}>
                                {`${"— ".repeat(target.depth)}${target.title} · ${target.kind}`}
                              </option>
                            ))}
                        </optgroup>
                        <optgroup label="Notes">
                          {relationTargetOptions
                            .filter((target) => target.group === "note")
                            .map((target) => (
                              <option key={target.id} value={target.id}>
                                {`${target.title} · ${target.kind}`}
                              </option>
                            ))}
                        </optgroup>
                      </select>
                    </div>
                  </div>

                  {content.trim() ? (
                    <div className="mt-3 rounded-lg border border-orange-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700">
                      <span className="font-bold text-focus">Preview:</span>{" "}
                      {plainTargetText(content, 100)}{" "}
                      <span className="text-muted">--{relationLabel(selectedRelation)}--&gt;</span>{" "}
                      {selectedRelationTarget?.title ?? "source đang chọn"}
                    </div>
                  ) : null}

                  {createRelationOpen ? (
                    <div className="mt-3 rounded-lg border border-orange-200 bg-white p-3">
                      <div className="grid gap-2 md:grid-cols-[160px_minmax(0,1fr)]">
                        <input
                          className="h-9 rounded-lg border border-line bg-slate-50 px-3 text-sm font-semibold text-ink"
                          value={newRelationSlug}
                          onChange={(event) => setNewRelationSlug(slugify(event.target.value))}
                          placeholder="slug_vi_du"
                          aria-label="Relation slug"
                        />
                        <input
                          className="h-9 rounded-lg border border-line bg-slate-50 px-3 text-sm font-semibold text-ink"
                          value={newRelationNameVi}
                          onChange={(event) => setNewRelationNameVi(event.target.value)}
                          placeholder="Tên tiếng Việt, ví dụ: dẫn tới"
                          aria-label="Relation Vietnamese name"
                        />
                        <input
                          className="h-9 rounded-lg border border-line bg-slate-50 px-3 text-sm font-semibold text-ink md:col-span-2"
                          value={newRelationDescriptionVi}
                          onChange={(event) => setNewRelationDescriptionVi(event.target.value)}
                          placeholder="Mô tả tiếng Việt để nhớ khi nào dùng relation này"
                          aria-label="Relation Vietnamese description"
                        />
                      </div>
                      <button
                        className="mt-2 inline-flex h-9 items-center gap-2 rounded-lg bg-brand px-3 text-xs font-bold text-white hover:bg-blue-700 disabled:bg-slate-300"
                        type="button"
                        disabled={!slugify(newRelationSlug) || !newRelationNameVi.trim() || isCreatingRelationType}
                        onClick={() => void handleCreateRelationType()}
                      >
                        <Plus size={14} aria-hidden="true" />
                        {isCreatingRelationType ? "Đang tạo" : "Tạo relation type"}
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </section>

      {(searchResults.length > 0 || searchError) && (
        <section className="rounded-lg border border-line bg-white shadow-panel">
          <div className="border-b border-line px-4 py-3">
            <h2 className="text-sm font-bold">Kết quả tìm kiếm</h2>
          </div>
          <div className="divide-y divide-line px-4">
            {searchError ? <p className="py-3 text-sm font-semibold text-red-700">{searchError}</p> : null}
            {searchResults.map((result) => (
              <article key={result.id} className="py-3">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="min-w-0 truncate text-sm font-bold">{result.title}</h3>
                  <span className="shrink-0 text-xs font-bold text-muted">{result.kind}</span>
                </div>
                <p className="mt-1 line-clamp-2 text-sm leading-6 text-slate-600">{result.content}</p>
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="rounded-lg border border-line bg-white shadow-panel">
        <div className="border-b border-line px-4 py-3">
          <h2 className="text-sm font-bold">Active notes</h2>
        </div>
        <div className="space-y-3 p-4">
          {aiError ? <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">{aiError}</p> : null}
          {visibleNotes.map((note) => (
            <article key={note.id} className="rounded-lg border border-line bg-white p-4">
              <div className="mb-3 flex flex-wrap items-start justify-end gap-2">
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                  <LifecycleBadge kind={note.kind} status={note.status} />
                  <StatusBadge kind={note.kind} />
                  <button
                    className={`inline-flex h-8 w-8 items-center justify-center rounded-lg border ${
                      editingNoteId === note.id ? "border-blue-200 bg-blue-50 text-brand" : "border-line bg-white text-slate-600 hover:bg-slate-50"
                    }`}
                    type="button"
                    aria-label="Sửa note"
                    title="Sửa note"
                    onClick={() => setEditingNoteId((current) => (current === note.id ? "" : note.id))}
                  >
                    <Pencil size={14} aria-hidden="true" />
                  </button>
                </div>
              </div>
              {editingNoteId === note.id ? (
                <NoteEditForm note={note} onSave={onUpdateNote} onCancel={() => setEditingNoteId("")} />
              ) : (
                <MarkdownPreview content={note.content} compact />
              )}
              <div className="mt-4 flex flex-wrap items-center gap-2 text-xs font-semibold text-muted">
                <span>{note.relationCount} relations</span>
                <span className="h-1 w-1 rounded-full bg-slate-300" />
                <span>{note.reviewCount} reviews</span>
                <span className="h-1 w-1 rounded-full bg-slate-300" />
                <span>{note.attachmentCount} files</span>
                <span className="h-1 w-1 rounded-full bg-slate-300" />
                <span>Tạo lúc {note.createdAt}</span>
                <span className="h-1 w-1 rounded-full bg-slate-300" />
                <span>Sửa {note.updatedAt}</span>
              </div>
              {(() => {
                const noteLinks = linksByObject[note.id] ?? note.links ?? [];
                if (noteLinks.length === 0) {
                  return null;
                }
                return (
                  <div className="mt-3 flex flex-wrap items-center gap-1.5">
                    {noteLinks.map((link) => (
                      <a
                        key={link.id}
                        href={link.url}
                        target="_blank"
                        rel="noreferrer"
                        title={link.url}
                        className="inline-flex h-7 max-w-[200px] items-center gap-1 rounded-md border border-blue-100 bg-blue-50 px-2 text-xs font-bold text-brand hover:bg-blue-100"
                      >
                        <ExternalLink size={12} aria-hidden="true" />
                        <span className="truncate">{link.label}</span>
                      </a>
                    ))}
                  </div>
                );
              })()}
              {editingNoteId === note.id ? (
                <div className="mt-4 border-t border-line pt-3">
                <div className="flex flex-wrap gap-2">
                  <button
                    className="inline-flex h-8 items-center gap-2 rounded-lg border border-line bg-white px-3 text-xs font-bold text-slate-700 hover:bg-slate-50"
                    type="button"
                    onClick={() => {
                      setConnectNoteId((current) => (current === note.id ? "" : note.id));
                      setConnectTargetId("");
                      setConnectTargetSearch("");
                    }}
                  >
                    <GitBranch size={14} aria-hidden="true" />
                    Connect
                  </button>
                  <button className="inline-flex h-8 items-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3 text-xs font-bold text-focus hover:bg-orange-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-muted" type="button" disabled={suggestingId === note.id} onClick={() => handleSuggest(note.id)}>
                    <Sparkles size={14} aria-hidden="true" />
                    {suggestingId === note.id ? "Đang gợi ý" : "Suggest"}
                  </button>
                  <button className="inline-flex h-8 items-center gap-2 rounded-lg border border-line bg-white px-3 text-xs font-bold text-slate-700 hover:bg-slate-50" type="button" onClick={() => void toggleAttachments(note.id)}>
                    <Paperclip size={14} aria-hidden="true" />
                    Files
                  </button>
                  <button className="inline-flex h-8 items-center gap-2 rounded-lg border border-line bg-white px-3 text-xs font-bold text-slate-700 hover:bg-slate-50" type="button" onClick={() => void toggleLinks(note.id)}>
                    <Link2 size={14} aria-hidden="true" />
                    Links
                  </button>
                  {onSetVisibility ? (
                    <button
                      className={`inline-flex h-8 items-center gap-2 rounded-lg border px-3 text-xs font-bold ${
                        note.visibility === "public"
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                          : "border-line bg-white text-slate-600 hover:bg-slate-50"
                      }`}
                      type="button"
                      title={
                        note.visibility === "public"
                          ? "Công khai: xuất hiện ở trang public. Bấm để chuyển về riêng tư."
                          : "Riêng tư: chỉ mình bạn thấy. Bấm để công khai."
                      }
                      onClick={() => void onSetVisibility(note.id, note.visibility === "public" ? "private" : "public")}
                    >
                      {note.visibility === "public" ? <Globe size={14} aria-hidden="true" /> : <Lock size={14} aria-hidden="true" />}
                      {note.visibility === "public" ? "Công khai" : "Riêng tư"}
                    </button>
                  ) : null}
                </div>

                {connectNoteId === note.id ? (
                  <div className="mt-3 rounded-lg border border-blue-100 bg-blue-50 p-3">
                    <div className="grid gap-3 lg:grid-cols-[220px_minmax(0,1fr)]">
                      <div>
                        <select className="h-9 w-full rounded-lg border border-blue-200 bg-white px-3 text-xs font-bold text-slate-700" value={connectType} onChange={(event) => setConnectType(event.target.value)} aria-label="Connect relation type">
                          {relationOptions.map((option) => (
                            <option key={option.id} value={option.slug}>
                              {relationLabel(option)}
                            </option>
                          ))}
                        </select>
                        {selectedConnectRelation ? (
                          <p className="mt-1 text-xs font-semibold text-slate-600">
                            {relationDescription(selectedConnectRelation) || selectedConnectRelation.slug}
                          </p>
                        ) : null}
                      </div>
                      <div className="space-y-2">
                        <input
                          className="h-9 w-full rounded-lg border border-blue-200 bg-white px-3 text-xs font-bold text-slate-700"
                          value={connectTargetSearch}
                          onChange={(event) => setConnectTargetSearch(event.target.value)}
                          placeholder="Tìm target theo note, source hoặc nội dung liên quan"
                          aria-label="Search connect target"
                        />
                        <div className="grid max-h-80 gap-2 overflow-y-auto pr-1 md:grid-cols-2">
                          {connectTargetLoading ? (
                            <p className="rounded-lg border border-blue-100 bg-white px-3 py-3 text-xs font-bold text-muted">
                              Đang tìm target...
                            </p>
                          ) : activeConnectTargetOptions.length === 0 ? (
                            <p className="rounded-lg border border-dashed border-blue-200 bg-white/70 px-3 py-3 text-xs font-bold text-muted">
                              Không có target phù hợp.
                            </p>
                          ) : (
                            activeConnectTargetOptions.map((target) => {
                              const selected = target.id === connectTargetId;
                              const targetExcerpt = plainTargetText(target.content);
                              const targetPrimaryText = isNoteLikeKind(target.kind) && targetExcerpt ? targetExcerpt : target.title;
                              const showTargetExcerpt = Boolean(targetExcerpt && targetPrimaryText !== targetExcerpt);
                              return (
                                <button
                                  key={target.id}
                                  className={`rounded-lg border bg-white p-3 text-left transition-colors ${
                                    selected ? "border-blue-400 ring-2 ring-blue-100" : "border-blue-100 hover:border-blue-300 hover:bg-blue-50"
                                  }`}
                                  type="button"
                                  onClick={() => setConnectTargetId(target.id)}
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <p className="line-clamp-2 text-sm font-bold leading-5 text-ink">{targetPrimaryText}</p>
                                    <span className="shrink-0 rounded-md border border-line bg-slate-50 px-2 py-0.5 text-[11px] font-bold text-muted">
                                      {target.kind}
                                    </span>
                                  </div>
                                  {target.context ? <p className="mt-1 line-clamp-1 text-xs font-semibold text-muted">{target.context}</p> : null}
                                  {showTargetExcerpt ? (
                                    <p className="mt-2 line-clamp-3 text-xs font-medium leading-5 text-slate-600">{targetExcerpt}</p>
                                  ) : null}
                                  {target.alreadyLinked ? (
                                    <span className="mt-2 inline-flex rounded-md border border-orange-200 bg-orange-50 px-2 py-0.5 text-[11px] font-bold text-focus">
                                      Đã có link
                                    </span>
                                  ) : null}
                                </button>
                              );
                            })
                          )}
                        </div>
                      </div>
                    </div>
                    {activeConnectNote && selectedConnectTarget ? (
                      <div className="mt-3 rounded-lg border border-blue-100 bg-white px-3 py-2 text-xs font-semibold text-slate-700">
                        <span className="font-bold text-brand">Preview:</span>{" "}
                        {plainTargetText(activeConnectNote.content, 90)}{" "}
                        <span className="text-muted">--{relationLabel(selectedConnectRelation)}--&gt;</span>{" "}
                        {selectedConnectTarget.title}
                      </div>
                    ) : null}
                    <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
                      <button className="inline-flex h-8 items-center gap-2 rounded-lg bg-brand px-3 text-xs font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300" type="button" disabled={!connectTargetId || isRelationSaving} onClick={() => handleCreateRelation(note.id)}>
                        <Link2 size={14} aria-hidden="true" />
                        {isRelationSaving ? "Đang lưu" : "Xác nhận nối"}
                      </button>
                    </div>
                    {relationError ? <p className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">{relationError}</p> : null}
                  </div>
                ) : null}

                {attachmentOpenId === note.id ? (
                  <div className="mt-3 rounded-lg border border-line bg-slate-50 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="text-xs font-bold text-slate-700">Files đính kèm</p>
                        <p className="text-[11px] font-medium text-muted">File đã lưu cùng note này.</p>
                      </div>
                    </div>
                    {attachmentError ? <p className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">{attachmentError}</p> : null}
                    {transcribeError ? <p className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">{transcribeError}</p> : null}
                    <div className="mt-3 space-y-2">
                      {(attachmentsByObject[note.id] ?? []).length === 0 ? (
                        <p className="text-xs font-semibold text-muted">Chưa có file nào.</p>
                      ) : (
                        attachmentsByObject[note.id].map((attachment) => {
                          const isImage = attachment.contentType?.startsWith("image/");
                          const viewUrl = attachment.viewUrl ?? `/api/attachments/${attachment.id}/view`;
                          const downloadUrl = attachment.downloadUrl ?? `/api/attachments/${attachment.id}/download`;

                          return (
                            <div key={attachment.id} className="flex items-center justify-between gap-3 rounded-md border border-line bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                              {isImage ? (
                                <a className="shrink-0" href={viewUrl} target="_blank" rel="noreferrer">
                                  <img className="h-14 w-14 rounded-md border border-line object-cover" src={viewUrl} alt={attachment.filename} />
                                </a>
                              ) : null}
                              <div className="min-w-0 flex-1">
                                <a className="block truncate hover:text-brand" href={isImage ? viewUrl : downloadUrl} target={isImage ? "_blank" : undefined} rel={isImage ? "noreferrer" : undefined}>
                                  {attachment.filename}
                                </a>
                                <span className="text-muted">{Math.ceil(attachment.sizeBytes / 1024)} KB</span>
                              </div>
                            {attachment.contentType?.startsWith("audio/") ? (
                              <button
                                className="shrink-0 inline-flex items-center gap-1 text-brand hover:text-blue-700 disabled:text-slate-300"
                                type="button"
                                disabled={transcribingId === attachment.id}
                                onClick={() => runTranscribe(note.id, attachment.id)}
                              >
                                {transcribingId === attachment.id ? (
                                  <Loader2 size={12} className="animate-spin" aria-hidden="true" />
                                ) : (
                                  <Sparkles size={12} aria-hidden="true" />
                                )}
                                {transcribingId === attachment.id ? "Đang transcript" : "Transcript"}
                              </button>
                            ) : null}
                            <button
                              className={`shrink-0 disabled:text-slate-300 ${
                                attachmentDeleteConfirm.pendingId === attachment.id ? "font-bold text-red-700" : "text-red-600 hover:text-red-700"
                              }`}
                              type="button"
                              disabled={uploadingId === note.id}
                              onClick={() => attachmentDeleteConfirm.request(attachment.id, () => handleDeleteAttachment(note.id, attachment.id))}
                            >
                              {attachmentDeleteConfirm.pendingId === attachment.id ? "Bấm lần nữa để xóa" : "Xóa"}
                            </button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                ) : null}

                {linkOpenId === note.id ? (
                  <EntityLinksPanel
                    objectId={note.id}
                    links={linksByObject[note.id] ?? []}
                    error={linkError}
                    onLoad={onLoadLinks}
                    onCreate={onCreateLink}
                    onDelete={onDeleteLink}
                  />
                ) : null}
                </div>
              ) : null}
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
